import { useQuery } from '@tanstack/react-query';
import { Caption1 } from '@fluentui/react-components';
import { isComplianceTracked, type Person, type PersonChecklist } from '@vigeo/shared';
import { StatusBadge } from '@vigeo/ui';
import { fetchHrWorkflow } from '@/features/hr-workflow';
import { statePresenters } from '@/features/readiness';
import { fetchPersonTimeline } from '@/features/timeline';
import { fetchCatalogOptions, fetchChecklist } from '@/lib/api';
import { useProfileStyles } from './parts.js';

/**
 * The reads the profile and the quick-look panel share. Every one is a route that already existed (#585), under the
 * query key its own screen uses where there is one, so the record page and this page do not ask twice.
 */

export type ComplianceRead =
  | { readonly status: 'untracked' }
  | { readonly status: 'pending' }
  | { readonly status: 'failed' }
  | { readonly status: 'ready'; readonly checklist: PersonChecklist };

/**
 * Where a person's compliance stands, DERIVED: the checklist the server works out on every read, never a stored value.
 * Staff added by hand are not tracked yet (ADR-016 §4), so no checklist is asked for and the answer is "untracked".
 * `enabled` false is a build that does not draw compliance (#723, `complianceAndDocumentsShown`): nobody is tracked
 * there, and no checklist is asked for.
 */
export function useCompliance(person: Person | undefined, enabled = true): ComplianceRead {
  const tracked = enabled && person !== undefined && isComplianceTracked(person);
  const checklist = useQuery({ queryKey: ['checklist', person?.id ?? ''], queryFn: () => fetchChecklist(person?.id ?? ''), enabled: tracked });
  if (person !== undefined && !tracked) return { status: 'untracked' };
  if (checklist.data !== undefined) return { status: 'ready', checklist: checklist.data };
  return checklist.isError ? { status: 'failed' } : { status: 'pending' };
}

/** The compliance state as the Roster shows it: a pill for a derived state, plain words for the rest. */
export function ComplianceMark({ compliance }: { readonly compliance: ComplianceRead }) {
  const styles = useProfileStyles();
  if (compliance.status === 'untracked') return <StatusBadge tone="neutral">Not tracked yet</StatusBadge>;
  if (compliance.status === 'ready') {
    const presenter = statePresenters[compliance.checklist.state];
    return <StatusBadge tone={presenter.tone}>{presenter.label}</StatusBadge>;
  }
  return <Caption1 className={styles.quiet}>{compliance.status === 'pending' ? 'Working it out…' : 'Unavailable'}</Caption1>;
}

/** `enabled` lets a page that shows roles only on one of its views read them only then; the key is shared, so the profile reuses the answer. */
export const useRoleOptions = (enabled = true) => useQuery({ queryKey: ['catalog-options'], queryFn: fetchCatalogOptions, enabled });
/** Same key as the record page's workflow read. */
export const useWorkflow = (personId: string, enabled = true) => useQuery({ queryKey: ['hr-workflow', personId], queryFn: () => fetchHrWorkflow(personId), enabled: enabled && personId !== '' });
/** The newest page of the person's history; only its first row is shown here. */
export const useHistoryHead = (personId: string, enabled = true) => useQuery({ queryKey: ['employee-profile', 'timeline', personId], queryFn: () => fetchPersonTimeline(personId), enabled: enabled && personId !== '', retry: false });
